import { useCallback, useState, type SetStateAction } from 'react';

/**
 * The JSON value stored under `key`, or null when there is none or it cannot
 * be read: storage can be blocked, and what it holds can be stale or corrupt.
 */
export function readStorage(key: string): unknown {
  try {
    const raw = localStorage.getItem(key);
    return raw === null ? null : JSON.parse(raw);
  } catch {
    return null;
  }
}

/** Stores `value` as JSON under `key`; a blocked or full storage is ignored. */
export function writeStorage(key: string, value: unknown) {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // A blocked or full storage costs the persistence, not the page.
  }
}

/**
 * A useState that writes every update to localStorage under `key`. `read`
 * builds the initial value from what is stored (null when nothing usable is),
 * so it is where a stored value gets validated. Per browser, not per user.
 */
export function usePersistedState<T>(
  key: string,
  read: (stored: unknown) => T,
) {
  const [value, setValue] = useState<T>(() => read(readStorage(key)));
  const setPersisted = useCallback(
    (updater: SetStateAction<T>) => {
      setValue((prev) => {
        const next =
          typeof updater === 'function'
            ? (updater as (prev: T) => T)(prev)
            : updater;
        writeStorage(key, next);
        return next;
      });
    },
    [key],
  );
  return [value, setPersisted] as const;
}
